/*
 * Custom Algolia search results controller (v0 redesign).
 * Paired with sections/search-results.liquid + assets/search-results.css
 *
 * Queries Algolia directly (REST multi-index) so the theme owns the raw response
 * objects (hits / nbHits / nbPages / query / userData / facets) and can render the
 * v0 tabbed UI, paginate every tab, filter the Products tab, and drive the client's
 * custom no-results messaging.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-search-results]');
  if (!root) return;

  var configEl = document.querySelector('[data-search-config]');
  var config = {};
  try {
    config = JSON.parse(configEl.textContent);
  } catch (e) {
    console.error('[search-results] invalid config', e);
    return;
  }

  var indices = config.indices || {};
  var PREVIEW = config.perSection || 6; // items shown per section in the "All Results" tab
  var showResources = !!config.showResources && !!indices.resources;
  var blogHandle = config.blogHandle || 'news';
  var searchUrl = config.searchUrl || '/search';
  var localeRoot = searchUrl.replace(/\/search\/?$/, ''); // '' for /search, '/en' for /en/search

  var PAGE_SIZE = {
    products: config.productsPerPage || 24,
    articles: config.articlesPerPage || 12,
    resources: 10,
    collections: 12,
  };

  /* ---------- Products facet configuration ---------- */
  // Algolia attributes we request facet counts for (must be in attributesForFaceting).
  var FACET_QUERY_ATTRS = ['vendor', 'tags', 'meta.custom.grade_level'];
  // Namespaced-tag facet groups: facet on `tags`, keep values with this prefix.
  var TAG_PREFIX = {
    learningPath: 'nav:learning-path:',
    designedFor: 'nav:designed-for:',
  };
  // Grade bands (v0). Each raw meta.custom.grade_level array (e.g. ["6","7","8"]) is
  // bucketed into every band whose tokens it overlaps; band count = sum of raw counts.
  var GRADE_BANDS = [
    { key: 'K-2', tokens: ['PreK', 'K', '1', '2'] },
    { key: '3-5', tokens: ['3', '4', '5'] },
    { key: '6-8', tokens: ['6', '7', '8'] },
    { key: '9-12', tokens: ['9', '10', '11', '12'] },
  ];
  // Sidebar groups, in v0 order. `label` is display text; underlying data noted.
  var FILTER_GROUPS = [
    { key: 'grade', label: 'Grade Level', kind: 'pills' }, // meta.custom.grade_level (bucketed)
    { key: 'learningPath', label: 'Subject', kind: 'check' }, // tags: nav:learning-path:*
    { key: 'brand', label: 'Brand', kind: 'check', limit: 6 }, // vendor
    { key: 'designedFor', label: 'Designed For', kind: 'check' }, // tags: nav:designed-for:*
  ];

  // DOM refs
  var input = root.querySelector('[data-search-input]');
  var form = root.querySelector('[data-search-form]');
  var clearBtn = root.querySelector('[data-search-clear]');
  var countEl = root.querySelector('[data-search-count]');
  var bannerEl = root.querySelector('[data-search-banner]');
  var bodyEl = root.querySelector('[data-search-body]');
  var tabs = Array.prototype.slice.call(root.querySelectorAll('[data-tab]'));
  var noResultsTextEl = root.querySelector('[data-noresults-text]');
  var layoutEl = root.querySelector('[data-layout]');
  var sidebarEl = root.querySelector('[data-facets]');
  var facetGroupsEl = root.querySelector('[data-facet-groups]');
  var activeFiltersEl = root.querySelector('[data-active-filters]');
  var filtersToggleEl = root.querySelector('[data-filters-toggle]');
  var states = {
    loading: root.querySelector('[data-state="loading"]'),
    empty: root.querySelector('[data-state="empty"]'),
    noresults: root.querySelector('[data-state="noresults"]'),
  };

  var SECTIONS = ['products', 'articles'];
  if (showResources) SECTIONS.push('resources');
  SECTIONS.push('collections');

  function emptyFilters() {
    return { grade: {}, learningPath: {}, brand: {}, designedFor: {} };
  }

  var state = {
    query: '',
    activeTab: 'all',
    responses: {},
    meta: {}, // section -> { nbHits, nbPages }
    pageCache: {}, // "section:page" -> hits[]
    currentPage: {}, // section -> page index
    total: 0,
    facets: null, // { grade:[], learningPath:[], brand:[], designedFor:[] }
    gradeRawMap: {}, // band key -> [raw grade_level values]
    filters: emptyFilters(), // group -> { value: true }
    productsTotal: null, // unfiltered products nbHits for the query
    expanded: {}, // group -> bool ("show all")
  };

  /* ---------------- helpers ---------------- */
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function stripHtml(s) {
    if (!s) return '';
    var d = document.createElement('div');
    d.innerHTML = s;
    return (d.textContent || '').replace(/\s+/g, ' ').trim();
  }

  function truncate(s, n) {
    s = s || '';
    return s.length > n ? s.slice(0, n).replace(/\s+\S*$/, '') + '…' : s;
  }

  function money(v) {
    if (v == null || isNaN(v)) return '';
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: config.currency || 'USD' }).format(v);
    } catch (e) {
      return '$' + Number(v).toFixed(2);
    }
  }

  function imgUrl(u, w) {
    if (!u) return '';
    return u + (u.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
  }

  function dedupeById(hits) {
    var seen = {};
    return hits.filter(function (h) {
      var key = h.id != null ? String(h.id) : h.objectID;
      if (seen[key]) return false;
      seen[key] = true;
      return true;
    });
  }

  function fmtDate(iso) {
    if (!iso) return '';
    var d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  }

  function icon(name) {
    var paths = {
      image:
        '<rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="m21 15-5-5L5 21"/>',
      document:
        '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/><path d="M8 13h8M8 17h8"/>',
      grid: '<rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>',
      chevron: '<path d="m9 18 6-6-6-6"/>',
      prev: '<path d="m15 18-6-6 6-6"/>',
      next: '<path d="m9 18 6-6-6-6"/>',
      sliders:
        '<path d="M4 6h11M18 6h2M4 12h2M9 12h11M4 18h8M15 18h5"/><circle cx="16" cy="6" r="2"/><circle cx="7" cy="12" r="2"/><circle cx="12" cy="18" r="2"/>',
      x: '<path d="M18 6 6 18M6 6l12 12"/>',
    };
    return (
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      paths[name] +
      '</svg>'
    );
  }

  /* ---------------- card templates ---------------- */
  function productCard(hit) {
    var url = localeRoot + '/products/' + encodeURIComponent(hit.handle);
    var img = hit.image || hit.product_image;
    var varies =
      hit.variants_min_price != null &&
      hit.variants_max_price != null &&
      hit.variants_min_price !== hit.variants_max_price;
    var price = varies ? 'From ' + money(hit.variants_min_price) : money(hit.price);
    var onSale = hit.compare_at_price && Number(hit.compare_at_price) > Number(hit.price);
    return (
      '<div class="sr-card">' +
      '<a class="sr-card__media" href="' +
      esc(url) +
      '" tabindex="-1" aria-hidden="true">' +
      (img
        ? '<img src="' + esc(imgUrl(img, 600)) + '" alt="" loading="lazy" width="600" height="450">'
        : '<span class="sr-card__placeholder">' + icon('image') + '</span>') +
      '</a>' +
      '<div class="sr-card__body">' +
      (hit.vendor ? '<span class="sr-card__eyebrow">' + esc(hit.vendor) + '</span>' : '') +
      '<h3 class="sr-card__title"><a href="' +
      esc(url) +
      '">' +
      esc(hit.title) +
      '</a></h3>' +
      '<div class="sr-card__meta"><span class="sr-card__price">' +
      esc(price) +
      (onSale ? '<del>' + esc(money(hit.compare_at_price)) + '</del>' : '') +
      '</span></div>' +
      '</div>' +
      '</div>'
    );
  }

  function articleCard(hit) {
    var url = localeRoot + '/blogs/' + encodeURIComponent(blogHandle) + '/' + encodeURIComponent(hit.handle);
    var img = hit.image;
    var cat = Array.isArray(hit.tags) && hit.tags.length ? hit.tags[0] : '';
    var date = fmtDate(hit.published_at || hit.created_at);
    var excerpt = truncate(stripHtml(hit.body_html_safe || hit.body_html), 120);
    return (
      '<a class="sr-card sr-card--article" href="' +
      esc(url) +
      '">' +
      '<div class="sr-card__media">' +
      (img
        ? '<img src="' + esc(imgUrl(img, 600)) + '" alt="" loading="lazy">'
        : '<span class="sr-card__placeholder">' + icon('image') + '</span>') +
      (cat ? '<span class="sr-card__badge">' + esc(cat) + '</span>' : '') +
      '</div>' +
      '<div class="sr-card__body">' +
      (date ? '<span class="sr-card__date">' + esc(date) + '</span>' : '') +
      '<h3 class="sr-card__title">' +
      esc(hit.title) +
      '</h3>' +
      (excerpt ? '<p class="sr-card__excerpt">' + esc(excerpt) + '</p>' : '') +
      '</div>' +
      '</a>'
    );
  }

  function resourceRow(hit) {
    var pageUrl = localeRoot + '/pages/' + encodeURIComponent(hit.handle);
    var fileUrl = hit.file_url || hit.url || pageUrl;
    var isDownload = !!hit.file_type || /\.(pdf|docx?|xlsx?|pptx?|zip|csv)$/i.test(fileUrl);
    var meta = hit.file_type
      ? String(hit.file_type).toUpperCase() + (hit.file_size ? ' · ' + hit.file_size : '')
      : hit.resource_type || 'Page';
    var label = isDownload ? 'Download' : 'View';
    return (
      '<div class="sr-resource">' +
      '<span class="sr-resource__icon">' +
      icon('document') +
      '</span>' +
      '<div class="sr-resource__info">' +
      '<p class="sr-resource__name">' +
      esc(hit.title) +
      '</p>' +
      '<p class="sr-resource__meta">' +
      esc(meta) +
      '</p>' +
      '</div>' +
      '<a class="sr-resource__action" href="' +
      esc(fileUrl) +
      '"' +
      (isDownload ? ' download' : '') +
      '>' +
      label +
      '</a>' +
      '</div>'
    );
  }

  function collectionCard(hit) {
    var url = localeRoot + '/collections/' + encodeURIComponent(hit.handle);
    var count = hit.products_count != null ? hit.products_count + ' products' : '';
    return (
      '<a class="sr-collection" href="' +
      esc(url) +
      '">' +
      '<span class="sr-collection__icon">' +
      icon('grid') +
      '</span>' +
      '<div class="sr-collection__info">' +
      '<p class="sr-collection__name">' +
      esc(hit.title) +
      '</p>' +
      (count ? '<p class="sr-collection__count">' + esc(count) + '</p>' : '') +
      '</div>' +
      '<span class="sr-collection__chevron">' +
      icon('chevron') +
      '</span>' +
      '</a>'
    );
  }

  var RENDERERS = {
    products: productCard,
    articles: articleCard,
    resources: resourceRow,
    collections: collectionCard,
  };

  /* ---------------- Algolia query ---------------- */
  function buildParams(query, n, page, withFilters) {
    var p = 'query=' + encodeURIComponent(query) + '&hitsPerPage=' + n + '&page=' + (page || 0);
    if (withFilters) {
      var ff = buildFacetFilters();
      if (ff.length) p += '&facetFilters=' + encodeURIComponent(JSON.stringify(ff));
    }
    return p;
  }

  function algoliaUrl() {
    return (
      'https://' +
      config.appId +
      '-dsn.algolia.net/1/indexes/*/queries?x-algolia-application-id=' +
      encodeURIComponent(config.appId) +
      '&x-algolia-api-key=' +
      encodeURIComponent(config.apiKey)
    );
  }

  function algoliaSearch(requests) {
    return fetch(algoliaUrl(), {
      method: 'POST',
      headers: { 'content-type': 'application/x-www-form-urlencoded' },
      body: JSON.stringify({ requests: requests }),
    }).then(function (r) {
      if (!r.ok) throw new Error('Algolia HTTP ' + r.status);
      return r.json();
    });
  }

  // Initial multi-index fetch (page 0). Products request also pulls facets (unfiltered).
  function fetchAll(query) {
    var requests = SECTIONS.map(function (section) {
      var params = buildParams(query, PAGE_SIZE[section], 0, false);
      if (section === 'products') {
        params += '&facets=' + encodeURIComponent(JSON.stringify(FACET_QUERY_ATTRS)) + '&maxValuesPerFacet=1000';
      }
      return { indexName: indices[section], params: params };
    });
    return algoliaSearch(requests).then(function (data) {
      var out = {};
      (data.results || []).forEach(function (res, i) {
        out[SECTIONS[i]] = res;
      });
      return out;
    });
  }

  // One page of a single index (products carries active facet filters).
  function fetchPage(section, page) {
    var params = buildParams(state.query, PAGE_SIZE[section], page, section === 'products');
    return algoliaSearch([{ indexName: indices[section], params: params }]).then(function (data) {
      return (data.results || [])[0] || {};
    });
  }

  function cacheKey(section, page) {
    return section + ':' + page;
  }

  function clearProductPageCache() {
    Object.keys(state.pageCache).forEach(function (k) {
      if (k.indexOf('products:') === 0) delete state.pageCache[k];
    });
  }

  /* ---------------- facets / filters ---------------- */
  function parseFacets(resp) {
    var f = (resp && resp.facets) || {};
    var vendor = f['vendor'] || {};
    var tags = f['tags'] || {};
    var grades = f['meta.custom.grade_level'] || {};

    function byCountDesc(a, b) {
      return b.count - a.count;
    }
    var brand = Object.keys(vendor)
      .map(function (v) {
        return { value: v, label: v, count: vendor[v] };
      })
      .sort(byCountDesc);

    function tagGroup(prefix) {
      return Object.keys(tags)
        .filter(function (t) {
          return t.indexOf(prefix) === 0;
        })
        .map(function (t) {
          return { value: t, label: t.slice(prefix.length).trim(), count: tags[t] };
        })
        .sort(byCountDesc);
    }

    state.gradeRawMap = {};
    var grade = GRADE_BANDS.map(function (band) {
      var count = 0;
      var raws = [];
      Object.keys(grades).forEach(function (raw) {
        var arr;
        try {
          arr = JSON.parse(raw);
        } catch (e) {
          return;
        }
        if (!Array.isArray(arr)) return;
        var overlaps = arr.some(function (g) {
          return band.tokens.indexOf(String(g)) > -1;
        });
        if (overlaps) {
          count += grades[raw];
          raws.push(raw);
        }
      });
      state.gradeRawMap[band.key] = raws;
      return { value: band.key, label: band.key, count: count };
    }).filter(function (b) {
      return b.count > 0;
    });

    state.facets = {
      grade: grade,
      learningPath: tagGroup(TAG_PREFIX.learningPath),
      brand: brand,
      designedFor: tagGroup(TAG_PREFIX.designedFor),
    };
  }

  function buildFacetFilters() {
    var ff = [];
    var brand = Object.keys(state.filters.brand);
    if (brand.length)
      ff.push(
        brand.map(function (v) {
          return 'vendor:' + v;
        })
      );
    var lp = Object.keys(state.filters.learningPath);
    if (lp.length)
      ff.push(
        lp.map(function (v) {
          return 'tags:' + v;
        })
      );
    var df = Object.keys(state.filters.designedFor);
    if (df.length)
      ff.push(
        df.map(function (v) {
          return 'tags:' + v;
        })
      );
    var bands = Object.keys(state.filters.grade);
    if (bands.length) {
      var raws = [];
      bands.forEach(function (b) {
        (state.gradeRawMap[b] || []).forEach(function (r) {
          raws.push('meta.custom.grade_level:' + r);
        });
      });
      if (raws.length) ff.push(raws);
    }
    return ff;
  }

  function activeFilterCount() {
    return Object.keys(state.filters).reduce(function (n, k) {
      return n + Object.keys(state.filters[k]).length;
    }, 0);
  }

  function labelForFilter(group, value) {
    if (group === 'grade' || group === 'brand') return value;
    var idx = value.lastIndexOf(':');
    return idx > -1 ? value.slice(idx + 1).trim() : value;
  }

  function renderSidebar() {
    if (!facetGroupsEl || !state.facets) return;
    var html = FILTER_GROUPS.map(function (g) {
      var opts = state.facets[g.key] || [];
      if (!opts.length) return '';
      var selected = state.filters[g.key];
      var body;
      if (g.kind === 'pills') {
        body =
          '<div class="sr-facet__pills">' +
          opts
            .map(function (o) {
              var on = !!selected[o.value];
              return (
                '<button type="button" class="sr-facet-pill' +
                (on ? ' is-on' : '') +
                '" data-facet="' +
                g.key +
                '" data-value="' +
                esc(o.value) +
                '" aria-pressed="' +
                (on ? 'true' : 'false') +
                '">' +
                esc(o.label) +
                '</button>'
              );
            })
            .join('') +
          '</div>';
      } else {
        var expanded = !!state.expanded[g.key];
        var limit = g.limit || 0;
        var extra = limit && opts.length > limit ? opts.length - limit : 0;
        body =
          '<div class="sr-facet__checks">' +
          opts
            .map(function (o, i) {
              var on = !!selected[o.value];
              var hidden = limit && !expanded && i >= limit ? ' hidden' : '';
              return (
                '<label class="sr-facet-check' +
                hidden +
                '">' +
                '<input type="checkbox" data-facet="' +
                g.key +
                '" data-value="' +
                esc(o.value) +
                '"' +
                (on ? ' checked' : '') +
                '>' +
                '<span class="sr-facet-check__label">' +
                esc(o.label) +
                '</span>' +
                '<span class="sr-facet-check__count">' +
                o.count +
                '</span>' +
                '</label>'
              );
            })
            .join('') +
          '</div>' +
          (extra
            ? '<button type="button" class="sr-facet__more" data-show-all="' +
              g.key +
              '">' +
              (expanded ? 'Show less' : 'Show all ' + g.label.toLowerCase() + ' (' + opts.length + ')') +
              '</button>'
            : '');
      }
      return (
        '<div class="sr-facet" data-facet-group="' +
        g.key +
        '">' +
        '<h3 class="sr-facet__title">' +
        esc(g.label) +
        '</h3>' +
        body +
        '</div>'
      );
    }).join('');
    facetGroupsEl.innerHTML = html;
  }

  function renderActiveFilters() {
    updateFiltersToggleLabel();
    if (!activeFiltersEl) return;
    var chips = [];
    Object.keys(state.filters).forEach(function (group) {
      Object.keys(state.filters[group]).forEach(function (value) {
        chips.push(
          '<button type="button" class="sr-chip" data-remove-facet="' +
            group +
            '" data-value="' +
            esc(value) +
            '">' +
            esc(labelForFilter(group, value)) +
            '<span class="sr-chip__x" aria-hidden="true">' +
            icon('x') +
            '</span>' +
            '</button>'
        );
      });
    });
    if (!chips.length) {
      activeFiltersEl.hidden = true;
      activeFiltersEl.innerHTML = '';
      return;
    }
    activeFiltersEl.innerHTML =
      chips.join('') + '<button type="button" class="sr-chip sr-chip--clear" data-clear-filters>Clear all</button>';
    activeFiltersEl.hidden = false;
  }

  function updateProductsCountLabel() {
    var el = root.querySelector('[data-group-count="products"]');
    if (!el) return;
    var m = state.meta.products || {};
    if (activeFilterCount() && state.productsTotal != null && m.nbHits !== state.productsTotal) {
      el.textContent = '(' + (m.nbHits || 0) + ' of ' + state.productsTotal + ')';
    } else {
      el.textContent = '(' + (m.nbHits || 0) + ')';
    }
  }

  function toggleFilter(group, value) {
    if (!state.filters[group]) return;
    if (state.filters[group][value]) delete state.filters[group][value];
    else state.filters[group][value] = true;
  }

  function applyProductFilters() {
    clearProductPageCache();
    state.currentPage.products = 0;
    fetchPage('products', 0)
      .then(function (resp) {
        state.pageCache[cacheKey('products', 0)] = resp.hits || [];
        state.meta.products = { nbHits: resp.nbHits || 0, nbPages: resp.nbPages || 0 };
        renderGroup('products');
        renderActiveFilters();
        updateProductsCountLabel();
      })
      .catch(function (err) {
        console.error('[search-results] filter query failed', err);
      });
  }

  function clearFilters() {
    state.filters = emptyFilters();
    renderSidebar();
    applyProductFilters();
  }

  /* ---------------- rendering ---------------- */
  function setState(name) {
    Object.keys(states).forEach(function (k) {
      if (states[k]) states[k].hidden = k !== name;
    });
    if (bodyEl) bodyEl.hidden = name !== null;
  }

  function setTabCount(tab, n) {
    var el = root.querySelector('[data-tab-count="' + tab + '"]');
    if (el) el.textContent = '(' + n + ')';
  }

  function resetCounts() {
    setTabCount('all', 0);
    SECTIONS.forEach(function (s) {
      setTabCount(s, 0);
    });
    if (countEl) countEl.hidden = true;
  }

  function pagerItems(cur, total) {
    var pages = {};
    pages[0] = 1;
    pages[total - 1] = 1;
    for (var i = cur - 1; i <= cur + 1; i++) if (i >= 0 && i < total) pages[i] = 1;
    var sorted = Object.keys(pages)
      .map(Number)
      .sort(function (a, b) {
        return a - b;
      });
    var items = [];
    var prev = -1;
    sorted.forEach(function (p) {
      if (prev >= 0 && p - prev > 1) items.push('…');
      items.push(p);
      prev = p;
    });
    return items;
  }

  function renderPagination(section, pagEl) {
    if (!pagEl) return;
    var meta = state.meta[section] || {};
    var nbPages = meta.nbPages || 0;
    if (nbPages <= 1) {
      pagEl.hidden = true;
      pagEl.innerHTML = '';
      return;
    }
    var cur = state.currentPage[section] || 0;
    var html =
      '<button type="button" class="search-results__page-btn search-results__page-btn--nav" data-page="' +
      (cur - 1) +
      '"' +
      (cur === 0 ? ' disabled' : '') +
      ' aria-label="Previous page">' +
      icon('prev') +
      '</button>';
    pagerItems(cur, nbPages).forEach(function (p) {
      if (p === '…') {
        html += '<span class="search-results__page-gap">…</span>';
      } else {
        html +=
          '<button type="button" class="search-results__page-btn' +
          (p === cur ? ' is-current' : '') +
          '" data-page="' +
          p +
          '"' +
          (p === cur ? ' aria-current="page"' : '') +
          '>' +
          (p + 1) +
          '</button>';
      }
    });
    html +=
      '<button type="button" class="search-results__page-btn search-results__page-btn--nav" data-page="' +
      (cur + 1) +
      '"' +
      (cur >= nbPages - 1 ? ' disabled' : '') +
      ' aria-label="Next page">' +
      icon('next') +
      '</button>';
    pagEl.innerHTML = html;
    pagEl.hidden = false;
  }

  // Show/hide the product filter sidebar. It applies to products, so it appears on the
  // "All Results" and "Products" tabs (and only when the query returned product facets).
  function updateSidebarChrome() {
    var show = (state.activeTab === 'all' || state.activeTab === 'products') && !!state.facets;
    if (layoutEl) layoutEl.classList.toggle('is-sidebar', show);
    if (sidebarEl) sidebarEl.hidden = !show;
    if (filtersToggleEl) filtersToggleEl.hidden = !show;
    if (show) {
      renderSidebar();
      renderActiveFilters();
    } else if (sidebarEl) {
      sidebarEl.classList.remove('is-open');
    }
  }

  function updateFiltersToggleLabel() {
    if (!filtersToggleEl) return;
    var n = activeFilterCount();
    filtersToggleEl.innerHTML = icon('sliders') + ' Filters' + (n ? ' (' + n + ')' : '');
  }

  function renderGroup(section) {
    var group = root.querySelector('[data-group="' + section + '"]');
    var container = root.querySelector('[data-results="' + section + '"]');
    var pagEl = root.querySelector('[data-pagination="' + section + '"]');
    var viewAll = root.querySelector('[data-view-all="' + section + '"]');
    if (!group || !container) return;

    var count = (state.meta[section] || {}).nbHits || 0;
    var active = state.activeTab;
    var visible = active === 'all' || active === section;

    // Section header count. Products shows "(X of Y)" when filtered (handled separately).
    if (section === 'products') {
      updateProductsCountLabel();
    } else {
      var groupCountEl = root.querySelector('[data-group-count="' + section + '"]');
      if (groupCountEl) groupCountEl.textContent = '(' + count + ')';
    }

    if (count === 0 || !visible) {
      // An active filter can legitimately yield 0 products — keep the products group
      // (and sidebar) visible on the All Results / Products tabs so filters can be adjusted.
      if (section === 'products' && (active === 'products' || active === 'all') && activeFilterCount() > 0) {
        group.hidden = false;
        container.innerHTML = '<p class="search-results__empty-filtered">No products match these filters.</p>';
        if (pagEl) {
          pagEl.hidden = true;
          pagEl.innerHTML = '';
        }
        updateProductsCountLabel();
        return;
      }
      group.hidden = true;
      container.innerHTML = '';
      if (pagEl) {
        pagEl.hidden = true;
        pagEl.innerHTML = '';
      }
      return;
    }
    group.hidden = false;

    if (active === 'all') {
      var preview = (state.pageCache[cacheKey(section, 0)] || []).slice();
      if (section === 'products') preview = dedupeById(preview);
      container.innerHTML = preview.slice(0, PREVIEW).map(RENDERERS[section]).join('');
      if (pagEl) {
        pagEl.hidden = true;
        pagEl.innerHTML = '';
      }
      if (viewAll) viewAll.hidden = count <= PREVIEW;
    } else {
      var page = state.currentPage[section] || 0;
      var hits = (state.pageCache[cacheKey(section, page)] || []).slice();
      if (section === 'products') hits = dedupeById(hits);
      container.innerHTML = hits.map(RENDERERS[section]).join('');
      renderPagination(section, pagEl);
      if (viewAll) viewAll.hidden = true;
      if (section === 'products') updateProductsCountLabel();
    }
  }

  function renderResults() {
    updateSidebarChrome();
    SECTIONS.forEach(renderGroup);
  }

  function updateCountLine() {
    if (!countEl) return;
    if (!state.query) {
      countEl.hidden = true;
      return;
    }
    countEl.innerHTML =
      'Found <strong>' + state.total + '</strong> results for <strong>"' + esc(state.query) + '"</strong>';
    countEl.hidden = false;
  }

  function scrollToTabs() {
    var nav = root.querySelector('[data-search-tabs]');
    if (nav && nav.scrollIntoView) nav.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /* ---------------- custom no-results messaging (client spec) ---------------- */
  function handleCustomMessaging() {
    if (!bannerEl) return false;
    bannerEl.innerHTML = '';
    bannerEl.hidden = true;

    var response = state.responses && state.responses.products; // Rule userData lives on products index
    var userData = response && response.userData && response.userData[0]; // Algolia returns userData as an array
    var isEmpty = state.total === 0;
    var queryStr = state.query;
    var suppress = false;

    // 1) Brand-not-carried + fallback
    if (isEmpty && userData && userData.noResultsMessage) {
      renderNoResultsBanner({
        message: userData.noResultsMessage,
        suggestedQuery: userData.suggestedQuery,
        suggestedCategory: userData.suggestedCategory,
        suggestedTag: userData.suggestedTag,
      });
      suppress = true;
    }

    // 2) Empty-query "popular products" view (optional flourish)
    if (queryStr === '' && userData && userData.displayHint === 'popular_products') {
      renderPopularProductsHeader();
    }

    // 3) ZIP code detection (no Algolia rule — pure frontend)
    if (isEmpty && /^\d{5}$/.test(queryStr)) {
      renderShippingBanner({ zipQuery: queryStr });
      suppress = true;
    }

    return suppress;
  }

  function showBanner(html) {
    bannerEl.innerHTML = html;
    bannerEl.hidden = false;
  }

  function renderNoResultsBanner(opts) {
    var actions = '';
    if (opts.suggestedQuery) {
      actions +=
        '<button type="button" class="sr-banner__link" data-suggest-query="' +
        esc(opts.suggestedQuery) +
        '">Search "' +
        esc(opts.suggestedQuery) +
        '"</button>';
    }
    if (opts.suggestedCategory) {
      actions +=
        '<a class="sr-banner__link" href="' +
        esc(localeRoot + '/collections/' + opts.suggestedCategory) +
        '">Browse ' +
        esc(opts.suggestedCategory.replace(/[-_]/g, ' ')) +
        '</a>';
    }
    if (opts.suggestedTag) {
      var tagLabel = String(opts.suggestedTag).split(':').pop();
      actions +=
        '<a class="sr-banner__link" href="' +
        esc(searchUrl + '?q=' + encodeURIComponent(tagLabel)) +
        '">Shop ' +
        esc(tagLabel) +
        '</a>';
    }
    showBanner(
      '<div class="sr-banner">' +
        '<p class="sr-banner__title">No exact matches</p>' +
        '<p class="sr-banner__text">' +
        esc(opts.message) +
        '</p>' +
        (actions ? '<div class="sr-banner__actions">' + actions + '</div>' : '') +
        '</div>'
    );
    var btn = bannerEl.querySelector('[data-suggest-query]');
    if (btn) {
      btn.addEventListener('click', function () {
        setQuery(btn.getAttribute('data-suggest-query'));
      });
    }
  }

  function renderPopularProductsHeader() {
    showBanner(
      '<div class="sr-banner sr-banner--popular">' +
        '<p class="sr-banner__title">Popular right now</p>' +
        '<p class="sr-banner__text">Here are some of our most popular products to get you started.</p>' +
        '</div>'
    );
  }

  function renderShippingBanner(opts) {
    showBanner(
      '<div class="sr-banner sr-banner--shipping">' +
        '<p class="sr-banner__title">Looking for shipping info?</p>' +
        '<p class="sr-banner__text">It looks like you entered a ZIP code (' +
        esc(opts.zipQuery) +
        '). We ship to schools and districts nationwide — contact us for delivery details to your area.</p>' +
        '</div>'
    );
  }

  /* ---------------- search flow ---------------- */
  function runSearch(query) {
    state.query = query;
    setClearVisible(query);

    if (!query && !config.queryEmpty) {
      state.responses = {};
      state.meta = {};
      state.pageCache = {};
      state.currentPage = {};
      state.total = 0;
      state.facets = null;
      state.filters = emptyFilters();
      resetCounts();
      if (bannerEl) {
        bannerEl.hidden = true;
        bannerEl.innerHTML = '';
      }
      setState('empty');
      return;
    }

    setState('loading');
    fetchAll(query)
      .then(function (responses) {
        state.responses = responses;
        state.meta = {};
        state.pageCache = {};
        state.currentPage = {};
        state.filters = emptyFilters();
        parseFacets(responses.products);
        var total = 0;
        SECTIONS.forEach(function (section) {
          var r = responses[section] || {};
          state.meta[section] = { nbHits: r.nbHits || 0, nbPages: r.nbPages || 0 };
          state.pageCache[cacheKey(section, 0)] = r.hits || [];
          state.currentPage[section] = 0;
          setTabCount(section, r.nbHits || 0);
          total += r.nbHits || 0;
        });
        state.productsTotal = (state.meta.products || {}).nbHits || 0;
        state.total = total;
        setTabCount('all', total);
        updateCountLine();

        if (total > 0) {
          setState(null);
          renderResults();
          handleCustomMessaging();
        } else {
          var suppressed = handleCustomMessaging();
          if (suppressed) {
            setState(null);
            if (bodyEl) bodyEl.hidden = true;
          } else {
            if (noResultsTextEl) {
              noResultsTextEl.textContent = 'We couldn’t find anything matching "' + query + '"';
            }
            setState('noresults');
          }
        }
      })
      .catch(function (err) {
        console.error('[search-results] search failed', err);
        state.total = 0;
        resetCounts();
        if (noResultsTextEl) noResultsTextEl.textContent = 'Something went wrong. Please try again.';
        setState('noresults');
      });
  }

  function goToPage(section, page) {
    var meta = state.meta[section] || {};
    var nbPages = meta.nbPages || 1;
    page = Math.max(0, Math.min(page, nbPages - 1));
    state.currentPage[section] = page;

    var key = cacheKey(section, page);
    if (state.pageCache[key]) {
      renderGroup(section);
      scrollToTabs();
      return;
    }
    fetchPage(section, page)
      .then(function (resp) {
        state.pageCache[key] = resp.hits || [];
        state.meta[section] = { nbHits: resp.nbHits || meta.nbHits || 0, nbPages: resp.nbPages || nbPages };
        renderGroup(section);
        scrollToTabs();
      })
      .catch(function (err) {
        console.error('[search-results] page fetch failed', err);
      });
  }

  function setActiveTab(tab) {
    state.activeTab = tab;
    tabs.forEach(function (t) {
      var isActive = t.getAttribute('data-tab') === tab;
      t.classList.toggle('is-active', isActive);
      t.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
    if (tab !== 'all' && state.currentPage[tab] == null) state.currentPage[tab] = 0;
    if (state.total > 0) renderResults();
  }

  function setQuery(query) {
    if (input) input.value = query;
    syncUrl(query);
    setActiveTab('all');
    runSearch(query);
  }

  function syncUrl(query) {
    try {
      var url = new URL(window.location.href);
      if (query) url.searchParams.set('q', query);
      else url.searchParams.delete('q');
      window.history.replaceState({}, '', url.toString());
    } catch (e) {
      /* no-op */
    }
  }

  function setClearVisible(query) {
    if (clearBtn) clearBtn.hidden = !query;
  }

  function getInitialQuery() {
    try {
      return new URL(window.location.href).searchParams.get('q') || '';
    } catch (e) {
      return '';
    }
  }

  /* ---------------- events ---------------- */
  var debounceTimer;
  if (input) {
    input.addEventListener('input', function () {
      var q = input.value.trim();
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(function () {
        syncUrl(q);
        setActiveTab('all');
        runSearch(q);
      }, 250);
    });
  }

  if (form) {
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      clearTimeout(debounceTimer);
      var q = input ? input.value.trim() : '';
      syncUrl(q);
      setActiveTab('all');
      runSearch(q);
    });
  }

  root.querySelectorAll('[data-search-clear]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      if (input) input.value = '';
      setQuery('');
      if (input) input.focus();
    });
  });

  tabs.forEach(function (t) {
    t.addEventListener('click', function () {
      setActiveTab(t.getAttribute('data-tab'));
      scrollToTabs();
    });
  });

  root.querySelectorAll('[data-view-all]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      setActiveTab(btn.getAttribute('data-view-all'));
      scrollToTabs();
    });
  });

  root.querySelectorAll('[data-pagination]').forEach(function (pagEl) {
    var section = pagEl.getAttribute('data-pagination');
    pagEl.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-page]');
      if (!btn || btn.disabled) return;
      goToPage(section, parseInt(btn.getAttribute('data-page'), 10));
    });
  });

  // Sidebar interactions (event delegation).
  if (sidebarEl) {
    sidebarEl.addEventListener('change', function (e) {
      var cb = e.target.closest('input[data-facet]');
      if (!cb) return;
      toggleFilter(cb.getAttribute('data-facet'), cb.getAttribute('data-value'));
      applyProductFilters();
    });
    sidebarEl.addEventListener('click', function (e) {
      var pill = e.target.closest('.sr-facet-pill[data-facet]');
      if (pill) {
        toggleFilter(pill.getAttribute('data-facet'), pill.getAttribute('data-value'));
        pill.classList.toggle('is-on');
        pill.setAttribute('aria-pressed', pill.classList.contains('is-on') ? 'true' : 'false');
        applyProductFilters();
        return;
      }
      var more = e.target.closest('[data-show-all]');
      if (more) {
        var g = more.getAttribute('data-show-all');
        state.expanded[g] = !state.expanded[g];
        renderSidebar();
      }
    });
  }

  if (activeFiltersEl) {
    activeFiltersEl.addEventListener('click', function (e) {
      if (e.target.closest('[data-clear-filters]')) {
        clearFilters();
        return;
      }
      var chip = e.target.closest('[data-remove-facet]');
      if (chip) {
        toggleFilter(chip.getAttribute('data-remove-facet'), chip.getAttribute('data-value'));
        renderSidebar();
        applyProductFilters();
      }
    });
  }

  if (filtersToggleEl && sidebarEl) {
    filtersToggleEl.addEventListener('click', function () {
      sidebarEl.classList.toggle('is-open');
    });
  }
  var sidebarCloseEl = root.querySelector('[data-facets-close]');
  if (sidebarCloseEl && sidebarEl) {
    sidebarCloseEl.addEventListener('click', function () {
      sidebarEl.classList.remove('is-open');
    });
  }

  /* ---------------- init ---------------- */
  var initialQuery = getInitialQuery();
  if (input && initialQuery) input.value = initialQuery;
  runSearch(initialQuery);
})();
